import React, { useEffect, useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import format from 'date-fns/format';
import { ru } from 'date-fns/locale';
import cx from 'classnames';
import {
  isDemoNewsSlug,
  isLayoutMock,
  MEDIA_RESERVED_REDIRECTS,
  routes,
  URL,
} from 'appConstants';
import {
  ButtonV4,
  HTMLContent,
  Link,
  Preloader,
} from 'components';
import { useRequest } from 'hooks';
import type { GetResponse, News } from 'types';
import { RequestStatus } from 'types';
import {
  dateToUiFormat,
  handleNoImage,
  _t,
  _t_back,
  parseNewsListResponse,
} from 'utils';

import banner from 'assets/img/main-v4/hero-curve.png';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';
import gallery1 from 'assets/img/news-v4/gallery-1.png';
import gallery2 from 'assets/img/news-v4/gallery-2.png';
import relatedImage from 'assets/img/news-v4/card-bg.png';

import styles from './styles.module.scss';

const RELATED_LIMIT = 3;

const TITLE_FALLBACK = (
  'Жители Китая познакомятся с продукцией из 43 регионов РФ ' +
  'на фестивале-ярмарке «Сделано в России»'
);

const LEAD_FALLBACK = (
  'Продукцию от российских производителей из 43 регионов представляют на ' +
  'фестивале-ярмарке «Сделано в России», которую Российский экспортный центр ' +
  '(Группа ВЭБ.РФ) организует в преддверии китайского Нового года в городе ' +
  'Шэньян, столице провинции Ляонин. Фестиваль проходит с 17 по 25 января в ' +
  'крупнейшем торговом центре города'
);

const BEFORE_GALLERY_FALLBACK = (
  'Всего жители Шэньяна могут оценить продукцию от более 120 производителей: ' +
  'продукты питания, напитки, косметику, сувениры и товары народного промысла. ' +
  'Так, например, на прилавках ярмарки можно будет найти молочную продукцию из ' +
  'Воронежской области, приморский мед, кондитерские изделия из Белгородской ' +
  'области и Кабардино-Балкарской Республики, владимирское печенье, бакалею из ' +
  'Амурской области, морсы из Карелии, медовые торты из Иркутской области ' +
  'косметику от новгородского производителя    и многое другое.'
);

const AFTER_GALLERY_FALLBACK = [
  'Гости могут попробовать настоящие русские блюда в кафе, оценить российскую ' +
  'продукцию в дегустационной зоне и сразу же приобрести понравившиеся продукты. ' +
  'Приобрести российскую продукцию также можно в национальных магазинах РЭЦ на ' +
  'китайских маркетплейсах. Кроме того, на площадке ярмарки компании проведут ' +
  'деловые встречи с представителями китайского бизнеса.',
  '\u00a0«Расширение знаний китайских потребителей и бизнеса о том, насколько ' +
  'разнообразную продукцию Россия готова поставлять, необходимо для реализации ' +
  'потенциала нашего торгового сотрудничества. Фестиваль-ярмарка “Сделано в ' +
  'России” предоставляет производителям площадку для того, чтобы оценить спрос ' +
  'на свою продукцию, найти покупателей и партнеров, сделать еще один шаг к ' +
  'высокой узнаваемости на новом рынке»,',
  'Напомним, Российский экспортный центр в 2024 году организовал три ' +
  'фестиваля-ярмарки «Сделано в России» в Китае. Так, в преддверии китайского ' +
  'Нового года первая ярмарка прошла в крупнейших городах Шэньян и Далянь в ' +
  'провинции Ляонин. Второе мероприятие состоялось в мае в городе Харбин, ' +
  'провинция Хэйлунцзян. Третья ярмарка прошла в ноябре в столице провинции ' +
  'Сычуань — городе Чэнду.\u00a0',
];

const formatCardDate = (value?: string) => {
  if (!value) {
    return '';
  }
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    return value;
  }
  return dateToUiFormat(value);
};

const formatArticleDate = (value?: string) => {
  if (!value) {
    return '';
  }
  if (/[а-яА-Я]/.test(value)) {
    return value;
  }
  const parsed = /^\d{2}\.\d{2}\.\d{4}$/.test(value)
    ? new Date(value.split('.').reverse().join('-'))
    : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return format(parsed, 'd MMMM yyyy', { locale: ru });
};

const MediaNewsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const reservedRedirect = id ? MEDIA_RESERVED_REDIRECTS[id] : undefined;
  const layoutMock = isLayoutMock() || isDemoNewsSlug(id);
  const { data, status, request } = useRequest<News>({
    errback: () => undefined,
  });
  const {
    data: relatedData,
    request: relatedRequest,
  } = useRequest<GetResponse<News[]> | News[]>({
    errback: () => undefined,
  });

  useEffect(() => {
    if (layoutMock || reservedRedirect) {
      return;
    }
    request({
      method: 'get',
      url: `${URL.NEWS.DETAILS.GET}/${id}`,
    });
    relatedRequest({
      method: 'get',
      url: URL.NEWS.GET,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showFallback = layoutMock || status === RequestStatus.ERROR || (
    status === RequestStatus.SUCCESS && !data
  );

  const title = showFallback ? _t(TITLE_FALLBACK) : _t_back(data, 'title');
  const lead = showFallback ? _t(LEAD_FALLBACK) : _t_back(data, 'lead');
  const beforeGallery = showFallback ? _t(BEFORE_GALLERY_FALLBACK) : '';
  const afterGallery = showFallback
    ? AFTER_GALLERY_FALLBACK.map((text) => _t(text))
    : [];
  const article = showFallback ? '' : _t_back(data, 'text_of_article');
  const pubdate = showFallback ? '2024-06-29' : data?.pubdate;
  let gallery: string[] = [];
  if (showFallback) {
    gallery = [gallery1, gallery2];
  } else if (data?.image) {
    gallery = [data.image];
  }

  const related = useMemo(() => {
    const items = layoutMock
      ? []
      : parseNewsListResponse(relatedData, {
        limit: RELATED_LIMIT + 5,
        offset: 0,
      }).results
        .filter((item) => (
          String(item.slug) !== String(id) &&
          item.id !== data?.id
        ))
        .slice(0, RELATED_LIMIT);

    if (items.length) {
      return items.map((item, index) => ({
        id: item.id,
        slug: item.slug,
        pubdate: item.pubdate,
        title: _t_back(item, 'title'),
        image: item.image || (index === 0 ? relatedImage : undefined),
      }));
    }

    return [
      {
        id: 1,
        slug: 'demo-news-3',
        pubdate: '09.04.2025',
        title: _t('Опрос о мерах поддержки «Международная кооперация и экспорт»'),
        image: relatedImage as string | undefined,
      },
      {
        id: 2,
        slug: 'demo-news-2',
        pubdate: '16.02.2026',
        title: _t('Положение о конгрессно-выставочных мероприятиях'),
        image: undefined as string | undefined,
      },
      {
        id: 3,
        slug: 'demo-news-4',
        pubdate: '06.03.2025',
        title: _t(
          'Объем поддержанного экспорта клиентов платформы «Мой экспорт» ' +
          'в 2024 году превысил 155 млрд рублей',
        ),
        image: undefined as string | undefined,
      },
    ];
  }, [relatedData, id, data?.id, layoutMock]);

  if (reservedRedirect) {
    return <Redirect to={reservedRedirect} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.banner} aria-hidden>
        <img src={banner} alt="" />
      </div>

      <Preloader isLoading={status === RequestStatus.REQUEST}>
        <section className={styles.head}>
          <h1 className={styles.title}>{title || _t('Новость')}</h1>
          <nav className={styles.crumbs} aria-label={_t('Навигация')}>
            <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
            <span className={styles.crumbLine} aria-hidden />
            <Link to={routes.media.news.root} className={styles.crumbLink}>
              {_t('Новости и медиа')}
            </Link>
            <span className={styles.crumbLine} aria-hidden />
            <span className={styles.crumbCurrent}>{_t('Обновления МОСПРОМ')}</span>
          </nav>
        </section>

        <div className={styles.layout}>
          <article className={styles.article}>
            <div className={styles.meta}>
              <span className={styles.badge}>{_t('НОВОСТИ')}</span>
              {pubdate ? (
                <time className={styles.date} dateTime={pubdate}>
                  {formatArticleDate(pubdate)}
                </time>
              ) : null}
            </div>

            <div className={styles.body}>
              {title ? <p className={styles.intro}>{title}</p> : null}
              {lead ? <p className={styles.lead}>{lead}</p> : null}
              {beforeGallery ? (
                <p className={styles.leadWide}>{beforeGallery}</p>
              ) : null}

              {gallery.length ? (
                <div className={styles.gallery}>
                  {gallery.map((src) => (
                    <img
                      key={src}
                      src={src}
                      className={gallery.length > 1 ? styles.photoSplit : styles.photo}
                      alt={title}
                      onError={handleNoImage}
                    />
                  ))}
                </div>
              ) : null}

              {afterGallery.map((text, index) => (
                <p
                  key={text.slice(0, 48)}
                  className={index === 0 ? styles.lead : styles.leadMid}
                >
                  {text}
                </p>
              ))}

              {article ? (
                <HTMLContent content={article} className={styles.html} />
              ) : null}
            </div>
          </article>

          <aside className={styles.aside}>
            <h2 className={styles.asideTitle}>{_t('Смотрите также')}</h2>
            <ul className={styles.asideList}>
              {related.map((item, index) => {
                const to = `${routes.media.root}/${item.slug || item.id}`;
                const tall = index === 0;
                const withImage = Boolean(item.image);

                return (
                  <li
                    key={item.id}
                    className={cx(
                      styles.asideCard,
                      tall ? styles.asideCardTall : styles.asideCardShort,
                      withImage && styles.asideCardImage,
                    )}
                    style={withImage ? {
                      ['--news-photo' as string]: `url(${item.image})`,
                    } : undefined}
                  >
                    <span className={styles.asideBadge}>{_t('НОВОСТИ')}</span>
                    <div className={styles.asideBody}>
                      <time className={styles.asideDate} dateTime={item.pubdate}>
                        {formatCardDate(item.pubdate)}
                      </time>
                      <div className={styles.asideCardTitle}>{item.title}</div>
                      <ButtonV4
                        to={to}
                        label="Подробнее"
                        variant="outline"
                        tone={withImage ? 'onDark' : 'onLight'}
                        iconSrc={iconArrow}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </Preloader>
    </div>
  );
};

export default MediaNewsDetails;
